import React, { useEffect, useState } from 'react';
import {
  adminGetContentByType,
  adminCreateContent,
  adminUpdateContent,
  adminSendTestEmail,
} from '../../api/adminApi';
import { ContentDocument, LegalArea, TranslationContent } from '../../api/contentApi';

const LANGS = ['de', 'fr', 'en'] as const;
type Lang = (typeof LANGS)[number];

const LANG_LABELS: Record<Lang, string> = { de: 'Deutsch', fr: 'Français', en: 'English' };

const PLACEHOLDER_HINT =
  'Verfügbare Platzhalter: {name}, {email}, {phone}, {areaOfLaw}, {subject}, {message}, {date}, {updateLink} (nur Bestätigung)';

const emptyTranslation = (): TranslationContent => ({
  isPublished: true,
  notificationSubject: '',
  notificationBodyHtml: '',
  notificationBodyText: '',
  confirmationSubject: '',
  confirmationBodyHtml: '',
  confirmationBodyText: '',
  bookingConfirmationSubject: '',
  bookingConfirmationBodyHtml: '',
  bookingConfirmationBodyText: '',
  bookingCancellationSubject: '',
  bookingCancellationBodyHtml: '',
  bookingCancellationBodyText: '',
  bookingNotificationSubject: '',
  bookingNotificationBodyHtml: '',
  bookingNotificationBodyText: '',
  bookingDocumentsSubject: '',
  bookingDocumentsBodyHtml: '',
  bookingDocumentsBodyText: '',
});

const BOOKING_PLACEHOLDER_HINT =
  'Verfügbare Platzhalter: {name}, {email}, {phone}, {message}, {appointmentType}, {date}, {time}, {updateLink}, {cancelLink}';

const GENERAL_AREA: LegalArea = { id: 'general', de: 'Standard (Fallback)', fr: 'Standard (Fallback)', en: 'General (Fallback)' };

/**
 * Admin page for managing email templates.
 *
 * Structure: one ContentDocument per legal area (slug = area id), plus a "general" fallback.
 * Each document stores notification + confirmation templates in all three languages.
 */
const AdminEmailTemplates: React.FC = () => {
  const [areas, setAreas] = useState<LegalArea[]>([GENERAL_AREA]);
  const [docs, setDocs] = useState<ContentDocument[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string>('general');
  const [activeLang, setActiveLang] = useState<Lang>('de');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Test email state
  const [testRecipient, setTestRecipient] = useState('test@example.com');
  const [testSending, setTestSending] = useState<'notification' | 'confirmation' | 'both' | null>(null);
  const [testResult, setTestResult] = useState<{ ok: true; sent: string[] } | { ok: false; message: string } | null>(null);

  // Local edits — keyed by areaId → lang → translation fields
  const [edits, setEdits] = useState<Record<string, Record<Lang, TranslationContent>>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load legal areas from settings
        const settingsDocs = await adminGetContentByType('settings');
        const settingsDoc = settingsDocs[0];
        const legalAreas: LegalArea[] = settingsDoc?.legalAreas ?? [];
        setAreas([GENERAL_AREA, ...legalAreas]);

        // Load all email-template documents
        const templateDocs = await adminGetContentByType('email-template');
        setDocs(templateDocs);

        // Initialise local edits from existing docs
        const initial: Record<string, Record<Lang, TranslationContent>> = {};
        for (const area of [GENERAL_AREA, ...legalAreas]) {
          const doc = templateDocs.find((d) => d.slug === area.id);
          initial[area.id] = {} as Record<Lang, TranslationContent>;
          for (const lang of LANGS) {
            initial[area.id][lang] = doc?.translations?.[lang]
              ? { ...emptyTranslation(), ...doc.translations[lang] }
              : emptyTranslation();
          }
        }
        setEdits(initial);
      } catch (err: any) {
        setSaveError(`Ladefehler: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getAreaLabel = (area: LegalArea): string =>
    area.de ?? area.en ?? area.id;

  const handleFieldChange = (
    areaId: string,
    lang: Lang,
    field: keyof TranslationContent,
    value: string
  ) => {
    setSaveSuccess(false);
    setEdits((prev) => ({
      ...prev,
      [areaId]: {
        ...prev[areaId],
        [lang]: { ...prev[areaId]?.[lang], [field]: value },
      },
    }));
  };

  const handleSave = async (areaId: string) => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      const existing = docs.find((d) => d.slug === areaId);
      const translations = Object.fromEntries(
        LANGS.map((lang) => [lang, edits[areaId]?.[lang] ?? emptyTranslation()])
      );

      if (existing) {
        const updated = await adminUpdateContent('email-template', existing.id, {
          ...existing,
          translations,
        });
        setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      } else {
        const created = await adminCreateContent({
          type: 'email-template',
          slug: areaId,
          isPublished: true,
          sortOrder: 0,
          translations,
        });
        setDocs((prev) => [...prev, created]);
      }
      setSaveSuccess(true);
    } catch (err: any) {
      setSaveError(`Fehler beim Speichern: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-gray-500">Laden…</p>;
  }

  const currentTranslation = edits[selectedAreaId]?.[activeLang] ?? emptyTranslation();
  const selectedArea = areas.find((a) => a.id === selectedAreaId);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">E-Mail Vorlagen</h1>
      <p className="text-sm text-gray-500 mb-6">{PLACEHOLDER_HINT}</p>

      <div className="flex flex-col md:flex-row gap-6">

        {/* Left — area list */}
        <aside className="w-full md:w-52 md:shrink-0">
          <nav className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {areas.map((area) => {
              const hasDoc = docs.some((d) => d.slug === area.id);
              return (
                <button
                  key={area.id}
                  onClick={() => { setSelectedAreaId(area.id); setSaveSuccess(false); setSaveError(''); }}
                  className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between transition ${
                    selectedAreaId === area.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{getAreaLabel(area)}</span>
                  {hasDoc ? (
                    <span className="text-green-500 text-xs" title="Vorlage vorhanden">●</span>
                  ) : (
                    <span className="text-gray-300 text-xs" title="Keine Vorlage">○</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right — editor */}
        <div className="flex-1 min-w-0">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800">
                {selectedArea ? getAreaLabel(selectedArea) : selectedAreaId}
              </h2>

              {/* Language tabs */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden text-sm">
                {LANGS.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`px-4 py-2 transition ${
                      activeLang === lang
                        ? 'bg-primary-600 text-white font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {LANG_LABELS[lang]}
                  </button>
                ))}
              </div>
            </div>

            {/* Notification template */}
            <section className="mb-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Benachrichtigung (→ Kanzlei)
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.notificationSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'notificationSubject', e.target.value)}
                    placeholder="Neue Anfrage von {name} — {areaOfLaw}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={6}
                    value={currentTranslation.notificationBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'notificationBodyHtml', e.target.value)}
                    placeholder="<p>Neue Anfrage von <strong>{name}</strong>…</p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={4}
                    value={currentTranslation.notificationBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'notificationBodyText', e.target.value)}
                    placeholder="Neue Anfrage von {name}…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            {/* Confirmation template */}
            <section className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Bestätigung (→ Kunde)
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.confirmationSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'confirmationSubject', e.target.value)}
                    placeholder="Ihre Anfrage ist eingegangen — {subject}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={8}
                    value={currentTranslation.confirmationBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'confirmationBodyHtml', e.target.value)}
                    placeholder="<p>Guten Tag {name},</p><p>wir haben Ihre Anfrage erhalten…</p><p><a href='{updateLink}'>Anfrage ergänzen</a></p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={5}
                    value={currentTranslation.confirmationBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'confirmationBodyText', e.target.value)}
                    placeholder="Guten Tag {name}, wir haben Ihre Anfrage erhalten…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            {/* Booking templates */}
            <h3 className="text-sm font-bold text-gray-700 mb-1 mt-8 pt-6 border-t border-gray-200">
              Terminbuchung
            </h3>
            <p className="text-xs text-gray-400 mb-4">{BOOKING_PLACEHOLDER_HINT}</p>

            <section className="mb-8">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Terminbestätigung (→ Kunde)
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.bookingConfirmationSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingConfirmationSubject', e.target.value)}
                    placeholder="Ihr Termin am {date} um {time} Uhr"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={8}
                    value={currentTranslation.bookingConfirmationBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingConfirmationBodyHtml', e.target.value)}
                    placeholder="<p>Guten Tag {name},</p><p>Ihr Termin ({appointmentType}) am {date} um {time} Uhr wurde bestätigt.</p><p><a href='{cancelLink}'>Termin verwalten/stornieren</a></p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={5}
                    value={currentTranslation.bookingConfirmationBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingConfirmationBodyText', e.target.value)}
                    placeholder="Guten Tag {name}, Ihr Termin ({appointmentType}) am {date} um {time} Uhr wurde bestätigt."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Terminabsage (→ Kunde)
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.bookingCancellationSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingCancellationSubject', e.target.value)}
                    placeholder="Ihr Termin am {date} wurde storniert"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={6}
                    value={currentTranslation.bookingCancellationBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingCancellationBodyHtml', e.target.value)}
                    placeholder="<p>Guten Tag {name},</p><p>Ihr Termin ({appointmentType}) am {date} um {time} Uhr wurde storniert.</p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={4}
                    value={currentTranslation.bookingCancellationBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingCancellationBodyText', e.target.value)}
                    placeholder="Guten Tag {name}, Ihr Termin ({appointmentType}) am {date} um {time} Uhr wurde storniert."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Neue Terminbuchung (→ Kanzlei)
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.bookingNotificationSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingNotificationSubject', e.target.value)}
                    placeholder="Neuer Termin: {appointmentType} am {date} um {time} Uhr"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={6}
                    value={currentTranslation.bookingNotificationBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingNotificationBodyHtml', e.target.value)}
                    placeholder="<p>Neuer Termin von <strong>{name}</strong> ({email}) — {appointmentType} am {date} um {time} Uhr.</p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={4}
                    value={currentTranslation.bookingNotificationBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingNotificationBodyText', e.target.value)}
                    placeholder="Neuer Termin von {name} ({email}) — {appointmentType} am {date} um {time} Uhr."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            <section className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dokumente-Nachfrage (→ Kunde)
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Betreff</label>
                  <input
                    type="text"
                    value={currentTranslation.bookingDocumentsSubject ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingDocumentsSubject', e.target.value)}
                    placeholder="Unterlagen zu Ihrem Termin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">HTML-Body</label>
                  <textarea
                    rows={6}
                    value={currentTranslation.bookingDocumentsBodyHtml ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingDocumentsBodyHtml', e.target.value)}
                    placeholder="<p>Guten Tag {name},</p><p>bitte laden Sie relevante Unterlagen hoch: <a href='{updateLink}'>Unterlagen hochladen</a></p>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plaintext-Body</label>
                  <textarea
                    rows={4}
                    value={currentTranslation.bookingDocumentsBodyText ?? ''}
                    onChange={(e) => handleFieldChange(selectedAreaId, activeLang, 'bookingDocumentsBodyText', e.target.value)}
                    placeholder="Guten Tag {name}, bitte laden Sie relevante Unterlagen hoch: {updateLink}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
                  />
                </div>
              </div>
            </section>

            {saveError && (
              <p className="text-sm text-red-600 mb-4">{saveError}</p>
            )}
            {saveSuccess && (
              <p className="text-sm text-green-600 mb-4">✓ Gespeichert</p>
            )}

            <button
              onClick={() => handleSave(selectedAreaId)}
              disabled={saving}
              className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50"
            >
              {saving ? 'Speichern…' : 'Speichern'}
            </button>

            {/* ── Test E-Mail ──────────────────────────────────────────── */}
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Test-E-Mail senden
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                Sendet eine Muster-Anfrage mit der aktuell ausgewählten Vorlage
                ({selectedArea ? getAreaLabel(selectedArea) : selectedAreaId} · {activeLang.toUpperCase()}).
                Die Benachrichtigung geht an die in den Einstellungen hinterlegte Kanzlei-Adresse,
                die Bestätigung an die unten eingetragene Adresse.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Empfänger (Bestätigung)</label>
                  <input
                    type="email"
                    value={testRecipient}
                    onChange={(e) => { setTestResult(null); setTestRecipient(e.target.value); }}
                    placeholder="test@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {(['notification', 'confirmation', 'both'] as const).map((type) => {
                  const labels = { notification: 'Benachrichtigung', confirmation: 'Bestätigung', both: 'Beide' };
                  const isSending = testSending === type;
                  return (
                    <button
                      key={type}
                      disabled={testSending !== null}
                      onClick={async () => {
                        setTestResult(null);
                        setTestSending(type);
                        try {
                          const result = await adminSendTestEmail({
                            type,
                            lang: activeLang,
                            areaOfLaw: selectedAreaId,
                            recipientEmail: testRecipient,
                          });
                          setTestResult({ ok: true, sent: result.sent });
                        } catch (err: any) {
                          setTestResult({ ok: false, message: err.message });
                        } finally {
                          setTestSending(null);
                        }
                      }}
                      className="inline-flex items-center gap-2 border border-gray-300 hover:border-primary-400 text-gray-700 hover:text-primary-700 text-sm px-4 py-2 rounded-lg transition disabled:opacity-50"
                    >
                      {isSending ? (
                        <span className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span>✉</span>
                      )}
                      {isSending ? 'Senden…' : labels[type]}
                    </button>
                  );
                })}
              </div>

              {testResult && (
                <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${
                  testResult.ok
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {testResult.ok
                    ? `✓ Gesendet: ${testResult.sent.join(', ')}`
                    : `✗ Fehler: ${testResult.message}`}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminEmailTemplates;
